import React, { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { useFieldArray } from "react-hook-form";

import {
  DSelect,
  HDatePicker,
  HInput,
  HTextarea,
  passParamsThrough,
} from "../../../../validation/HForm";
import useServiceInformation from "./hooks/useServiceInfirmation";
import { calculateRisk } from "../../modal.utils";
import { maxLength } from "../../../../helpers/data-constants";
import { formattingDate } from "../../../../helpers/common";
import { CLIENT_FIELDS, useDefaultValues } from "./constants";
import { RiskAssessment } from "./RiskAssessment";
import {OperationFields} from "./OperationFields";
import CharCount from "../../CharCount/CharCount";
import FormElementsAdd from "../FormElementsAdd";
import DeleteIcon from "../../../../assets/icons/trash.svg";

import "./serviceInformation.scss";

const ServiceInformationAdd = (props) => {
  const { tempEntity, clientId, setValue, watch, control, description, handleAdditionalChange } = props;
  const { accessFeatures } = useSelector(({user}) => user);
  const { defaultFurtherAction, defaultBlockDecision, defaultSuspiciousOperation } = useDefaultValues();
  const aimAndRelationshipFeature = accessFeatures['client.aim_and_relationship']

  const furtherActions = useFieldArray({
    control,
    name: "furtherActions",
    keyName: "_id",
  });

  const blockDecisions = useFieldArray({
    control,
    name: "blockTransactions",
    keyName: "_id",
  });

  const suspiciousOperations = useFieldArray({
    control,
    name: "suspiciousTransactions",
    keyName: "_id",
  });

  const { descriptions, employees, setDescriptions, fetchEmployees, handleGetDealNumber } =
    useServiceInformation({ setValue, tempEntity, clientId });

  const handleDescriptionChange = useCallback(
    (type) => (e) => {
      setDescriptions((prev) => ({ ...prev, [type]: e.target.value }));
    },[]);

    const handleAddDefaultAction = (fieldArray, defaultValue) => {
      if (fieldArray.fields.length === 0) {
        fieldArray.append(defaultValue);
      }
    };

  const riskGeographic = watch("riskGeographic");
  const riskOperationType = watch("riskOperationType");
  const riskProfile = watch("riskProfile");

  useEffect(() => {
    if (!aimAndRelationshipFeature) {
      const risk = calculateRisk(riskGeographic, riskOperationType, riskProfile);
      setValue("riskFinal", risk ? "Низкий" : "Высокий");
    }
  }, [riskGeographic, riskOperationType, riskProfile, setValue]);
  
    useEffect(() => {
      handleAddDefaultAction(suspiciousOperations, defaultSuspiciousOperation);
      handleAddDefaultAction(blockDecisions, defaultBlockDecision);
      handleAddDefaultAction(furtherActions, defaultFurtherAction);
    }, [suspiciousOperations.fields.length, blockDecisions.fields.length, furtherActions.fields.length]);

  return passParamsThrough(
    <fieldset className="serviceInformationAdd">
      <div className="modal-content">
        <RiskAssessment
          h="true"
          watch={watch}
          setValue={setValue}
          descriptions={descriptions}
          handleDescriptionChange={handleDescriptionChange}
        />

        <div className="action-section">
          <div className="d-flex space-between">
            <h4>
              Решение ОДЛ о дальнейших действиях в отношении клиента,
              предусмотренных {aimAndRelationshipFeature ? "п.50" : "п.8.4"} Правил внутреннего контроля в сфере ПОД/ФТ, и
              сроки его выполнения
            </h4>
            <button
              className="action-item"
              type="button"
              onClick={() => furtherActions.append(defaultFurtherAction)}
            >
              Добавить запись
            </button>
          </div>
          {furtherActions.fields.map((field, index) => (
            <div key={field._id} className="entry-wrapper">
              {furtherActions.fields.length > 1 && (
                <button
                  type="button"
                  className="button-icontrigger button-icontrigger-absolute"
                  onClick={() => furtherActions.remove(index)}
                >
                  <span className="icon">
                    <DeleteIcon />
                  </span>
                </button>
              )}
              <div className="serviceInformationAdd-grid-container grid-cols-4">
                <div className="col-span-2">
                  <DSelect
                    h="true"
                    name={`furtherActions.${index}.fullName`}
                    placeholder="Введите ФИО"
                    options={employees}
                    handleOptions={fetchEmployees}
                    formatCreateLabel={(inputValue) => inputValue}
                    noOptionsMessage={() => "Нет результатов"}
                    defaultValue={field.fullName}
                    isCreatable
                    isClearable
                  />
                </div>
                <div className="col-span-1">
                  <label className="form-element-label">Дата решения</label>
                  <HDatePicker
                    h="true"
                    className="input"
                    name={`furtherActions.${index}.dateDecision`}
                    defaultValue={field.dateDecision ? formattingDate(field.dateDecision) : null}
                  />
                </div>
                <div className="col-span-1">
                  <label className="form-element-label">Срок выполнения</label>
                  <HDatePicker
                    h="true"
                    className="input"
                    name={`furtherActions.${index}.dateCompletion`}
                    defaultValue={field.dateCompletion ? formattingDate(field.dateCompletion) : null}
                  />
                </div>
              </div>
              <div className="risk-item">
                <div className="textarea-container">
                  <HTextarea
                    h="true"
                    className="input"
                    rows="3"
                    cols="10"
                    name={`furtherActions.${index}.decision`}
                    placeholder="Решение"
                    defaultValue={field.decision}
                  />
                  <CharCount
                    value={watch(`furtherActions.${index}.decision`) || ""}
                    maxLength={maxLength.SERVICE_INFORMATION_RISK}
                  />
                </div>
              </div>
              <HInput
                h="true"
                type="hidden"
                name={`furtherActions.${index}.id`}
                defaultValue={field.id}
              />
            </div>
          ))}
        </div>

        <div className="action-section">
          <div className="d-flex space-between">
            <h4>
              Решение ОДЛ о замораживании средств и (или) блокировании
              финансовой операции в отношении лиц, причастных к террористической
              деятельности
            </h4>
            <button
              className="action-item"
              type="button"
              onClick={() => blockDecisions.append(defaultBlockDecision)}
            >
              Добавить запись
            </button>
          </div>
          {blockDecisions.fields.map((field, index) => (
            <div key={field._id} className="entry-wrapper">
              {blockDecisions.fields.length > 1 && (
                <button
                  type="button"
                  className="button-icontrigger button-icontrigger-absolute"
                  onClick={() => blockDecisions.remove(index)}
                >
                  <span className="icon">
                    <DeleteIcon />
                  </span>
                </button>
              )}
              <div className="serviceInformationAdd-grid-container grid-cols-4">
                <div className="col-span-2">
                  <DSelect
                    h="true"
                    name={`blockTransactions.${index}.fullName`}
                    placeholder="Введите ФИО"
                    options={employees}
                    handleOptions={fetchEmployees}
                    formatCreateLabel={(inputValue) => inputValue}
                    noOptionsMessage={() => "Нет результатов"}
                    defaultValue={field.fullName}
                    isCreatable
                    isClearable
                  />
                </div>
                <div className="col-span-2">
                  <label className="form-element-label">Дата решения</label>
                  <HDatePicker
                    h="true"
                    className="input"
                    name={`blockTransactions.${index}.dateDecision`}
                    defaultValue={
                      field.dateDecision
                        ? formattingDate(field.dateDecision)
                        : null
                    }
                  />
                </div>
              </div>
              <div className="risk-item">
                <div className="textarea-container">
                  <HTextarea
                    h="true"
                    className="input"
                    rows="3"
                    cols="10"
                    name={`blockTransactions.${index}.decision`}
                    placeholder="Решение"
                    defaultValue={field.decision}
                  />
                  <CharCount
                    value={watch(`blockTransactions.${index}.decision`) || ""}
                    maxLength={maxLength.SERVICE_INFORMATION_RISK}
                  />
                </div>
              </div>
              <HInput
                h="true"
                type="hidden"
                name={`blockTransactions.${index}.id`}
                defaultValue={field.id}
              />
            </div>
          ))}
        </div>
        <div className="action-section">
          <div className="d-flex flex-space-between">
            <h4>Сведения о подозрительных финансовых операциях клиента</h4>
            <button
              className="action-item"
              type="button"
              onClick={() =>
                suspiciousOperations.append(defaultSuspiciousOperation)
              }
            >
              Добавить запись
            </button>
          </div>
          {suspiciousOperations.fields.map((field, index) => (
            <div key={field._id} className="entry-wrapper">
              {suspiciousOperations.fields.length > 1 && (
                <button
                  type="button"
                  className="button-icontrigger button-icontrigger-absolute"
                  onClick={() => suspiciousOperations.remove(index)}
                >
                  <span className="icon">
                    <DeleteIcon />
                  </span>
                </button>
              )}
              <div className="serviceInformationAdd-grid-container grid-cols-2">
                <div className="col-span-1">
                  <label className="form-element-label">
                    Номер специального формуляра
                  </label>
                  <HInput
                    h="true"
                    className="input block-field"
                    type="text"
                    name={`suspiciousTransactions.${index}.specialForm`}
                    defaultValue={field.specialForm}
                  />
                </div>
              </div>
              <div className="serviceInformationAdd-grid-container grid-cols-2">
                <div className="col-span-1">
                  <label className="form-element-label">
                    Код признака подозрительности
                  </label>
                  <HInput
                    h="true"
                    className="input block-field"
                    type="text"
                    name={`suspiciousTransactions.${index}.codeSuspicion`}
                    placeholder="Введите код"
                    defaultValue={field.codeSuspicion}
                  />
                </div>
                <div className="col-span-1">
                  <label className="form-element-label">Дата направления</label>
                  <HDatePicker
                    h="true"
                    className="input"
                    name={`suspiciousTransactions.${index}.referralDate`}
                    defaultValue={
                      field.referralDate
                        ? formattingDate(field.referralDate)
                        : null
                    }
                  />
                </div>
                <OperationFields h="true" nestedIndex={index} control={control} handleGetDealNumber={handleGetDealNumber} />
                <HInput
                  h="true"
                  type="hidden"
                  name={`suspiciousTransactions.${index}.id`}
                  defaultValue={field.id}
              />
              </div>
            </div>
          ))}
        </div>
        {!aimAndRelationshipFeature ? <div className="action-section">
          <FormElementsAdd 
            h="true"
            isServiceInfo
            description={description} 
            handleAdditionalChange={handleAdditionalChange}
            />
        </div> : 
        <div className="action-section">
          <FormElementsAdd 
            h="true"
            isServiceInfo
            description={description} 
            currentField={CLIENT_FIELDS.LACK_IDENTIFIER_DATA}
            handleAdditionalChange={handleAdditionalChange}
            />
        </div>}
      </div>
    </fieldset>,
    props
  );
};

export default ServiceInformationAdd;
